import { GAUNTLET_LEVEL } from '~/data/ascensions'

/** Meta-progress: highest ascension UNLOCKED (0 = only Normal), separate
 * from run saves so it survives New Run / cleared runs. */
const UNLOCK_KEY = 'elemental-dispatch-ascension-unlocked'
/** Last ascension the player picked on the menu (restored next visit). */
const CHOICE_KEY = 'elemental-dispatch-ascension-choice'
/** 🏅 Levels already cleared at least once — the first-clear bounty is
 * paid exactly once per level, ever (v3.62). A grow-only list, so it
 * merges across devices by union like the codex. */
const CLEARED_KEY = 'elemental-dispatch-ascension-cleared'

export function useAscension() {
  const unlocked = useState('ascension-unlocked', () => 0)
  const choice = useState('ascension-choice', () => 0)
  const cleared = useState<number[]>('ascension-cleared', () => [])

  function load() {
    if (typeof localStorage === 'undefined') return
    // ☁️ v7.17 (follow-up review): assign only what CHANGED. Every adopter
    // calls this, and re-assigning an equal `cleared` array replaced the
    // ref each time - the cloud watcher (deep) read that as local progress,
    // every push absorbs, and an idle signed-in session pushed every two
    // seconds, forever.
    const nextUnlocked = clamp(Number(localStorage.getItem(UNLOCK_KEY) ?? 0))
    if (nextUnlocked !== unlocked.value) unlocked.value = nextUnlocked
    const nextChoice = Math.min(clamp(Number(localStorage.getItem(CHOICE_KEY) ?? 0)), nextUnlocked)
    if (nextChoice !== choice.value) choice.value = nextChoice
    let list: number[] = []
    try {
      const raw = JSON.parse(localStorage.getItem(CLEARED_KEY) ?? '[]') as unknown
      list = Array.isArray(raw) ? raw.filter(n => Number.isFinite(n)).map(Number) : []
    } catch { list = [] }
    if (list.length !== cleared.value.length || list.some((n, i) => n !== cleared.value[i])) cleared.value = list
  }

  function persistCleared() {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(CLEARED_KEY, JSON.stringify(cleared.value))
    }
  }

  /**
   * 🏅 Marks a level cleared. Returns TRUE only the first time, which is
   * what gates the bounty — the caller pays out on a true and never
   * again, even across devices, because the list syncs by union.
   */
  function markCleared(level: number): boolean {
    load()
    const lv = clamp(level)
    if (cleared.value.includes(lv)) return false
    cleared.value = [...cleared.value, lv]
    persistCleared()
    return true
  }

  /** ☁️ Union-merge the cleared list from another device. */
  function adoptCleared(remote: number[]) {
    load()
    if (!Array.isArray(remote)) return
    const merged = [...new Set([...cleared.value, ...remote.filter(n => Number.isFinite(n)).map(Number)])]
    if (merged.length !== cleared.value.length) {
      cleared.value = merged
      persistCleared()
    }
  }

  function select(level: number) {
    choice.value = Math.min(clamp(level), unlocked.value)
    if (typeof localStorage !== 'undefined') localStorage.setItem(CHOICE_KEY, String(choice.value))
  }

  /** Call when a run is WON: winning on level N unlocks N+1. */
  function recordWin(ascension: number) {
    load() // never regress a stored unlock the session hasn't read yet
    const next = clamp(ascension + 1)
    if (next > unlocked.value) {
      unlocked.value = next
      if (typeof localStorage !== 'undefined') localStorage.setItem(UNLOCK_KEY, String(next))
    }
  }

  /** ☁️ Cloud sync (v1.17): adopt a remote unlock when it's ahead of
   * this device — progress only ever moves up. */
  function adoptUnlocked(n: number) {
    load()
    const next = clamp(n)
    if (next > unlocked.value) {
      unlocked.value = next
      if (typeof localStorage !== 'undefined') localStorage.setItem(UNLOCK_KEY, String(next))
    }
  }

  function clamp(n: number) {
    // ⚔️ Level 11 = Gauntlet unlocked (v2.34): beating A10 stores 11, so
    // "unlocked A10" and "beat A10" stay distinguishable.
    return Number.isFinite(n) ? Math.max(0, Math.min(GAUNTLET_LEVEL, Math.floor(n))) : 0
  }

  return { unlocked, choice, cleared, load, select, recordWin, adoptUnlocked, markCleared, adoptCleared }
}
